/**
 * The document head for the glossary.
 *
 * `DefinedTerm` inside a `DefinedTermSet`, which is the vocabulary schema.org
 * actually has for a glossary — not `Article`, which would assert authorship
 * and a publication date for a definition, and not `FAQPage`, which would claim
 * a question-and-answer structure these pages do not have.
 *
 * Whether Google renders anything from `DefinedTerm` is beside the point: the
 * markup is read by more than Google, and the correct type costs the same as
 * the wrong one.
 */

import type { ContentLang } from "@fazly/shared/contentLang";

import {
  alternates,
  breadcrumbJsonLd,
  canonical,
  organizationJsonLd,
  socialMeta,
} from "../seo";
import { GLOSSARY_SLUGS, glossaryIndexPath, glossaryPath } from "./index";
import type { GlossaryBundle, GlossaryEntry, GlossarySlug } from "./types";
import type { GlossaryUi } from "./ui";

function definedTerm(
  slug: GlossarySlug,
  lang: ContentLang,
  entry: GlossaryEntry,
) {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: entry.term,
    description: entry.answer,
    url: canonical(glossaryPath(slug, lang)),
    inLanguage: lang,
    inDefinedTermSet: {
      "@type": "DefinedTermSet",
      name: "Fazly looksmax glossary",
      url: canonical(glossaryIndexPath(lang)),
    },
  };
}

export function glossaryHead(
  slug: GlossarySlug,
  lang: ContentLang,
  entry: GlossaryEntry,
  ui: GlossaryUi,
) {
  const path = glossaryPath(slug, lang);
  return {
    meta: [
      { title: entry.title },
      { name: "description", content: entry.description },
      ...socialMeta({
        title: entry.title,
        description: entry.description,
        path,
        lang,
        imageAlt: entry.term,
      }),
      { "script:ld+json": definedTerm(slug, lang, entry) },
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Fazly", path: lang === "en" ? "/" : `/${lang}` },
          { name: ui.indexH1, path: glossaryIndexPath(lang) },
          { name: entry.term, path },
        ]),
      },
      { "script:ld+json": organizationJsonLd() },
    ],
    links: [
      { rel: "canonical", href: canonical(path) },
      ...alternates((l) => glossaryPath(slug, l)),
    ],
  };
}

export function glossaryIndexHead(
  lang: ContentLang,
  ui: GlossaryUi,
  bundle: GlossaryBundle,
) {
  const path = glossaryIndexPath(lang);
  return {
    meta: [
      { title: ui.indexTitle },
      { name: "description", content: ui.indexDescription },
      ...socialMeta({
        title: ui.indexTitle,
        description: ui.indexDescription,
        path,
        lang,
        imageAlt: ui.indexH1,
      }),
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          name: ui.indexH1,
          description: ui.indexDescription,
          url: canonical(path),
          inLanguage: lang,
          hasDefinedTerm: GLOSSARY_SLUGS.map((slug) => ({
            "@type": "DefinedTerm",
            name: bundle[slug].term,
            url: canonical(glossaryPath(slug, lang)),
          })),
        },
      },
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Fazly", path: lang === "en" ? "/" : `/${lang}` },
          { name: ui.indexH1, path },
        ]),
      },
    ],
    links: [
      { rel: "canonical", href: canonical(path) },
      ...alternates(glossaryIndexPath),
    ],
  };
}
